import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/Print";
import {Alert, Box, Button, Dialog, IconButton, Skeleton, Stack, Typography, useMediaQuery, useTheme} from "@mui/material";
import React, {useLayoutEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {DigitalSigningStatusElement} from "../../common/uiElements/DigitalSigningStatusElement";
import {FOLIO_DIALOG_MAX_WIDTH} from "../../system/billing/folio/editor/folioStyles";
import {DigitalSigningDetailsView} from "../digitalSigning.model";
import {DigitalSigningService} from "../digitalSigning.service";
import {digitalSigningDetailsKey} from "../digitalSigningQueries";
import {SIGNING_PAPER} from "../public/signingTheme";
import DigitalSigningSignedView from "../public/DigitalSigningSignedView";
import {signedPageOf} from "./digitalSigningDetails.util";
import DigitalSigningHistory from "./DigitalSigningHistory";

export const DIGITAL_SIGNING_DETAILS_TITLE_ID = 'digital-signing-details-title';
const HISTORY_TITLE_ID = 'digital-signing-details-history';

const PAPER_SX = {
    maxWidth: FOLIO_DIALOG_MAX_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '& .MuiTypography-caption': {fontSize: 12, lineHeight: '16px'},
};

const PAGE_WIDTH = 600 + 2 * 24;

const SECTION_TITLE_SX = {
    m: 0,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
};

interface Props {
    id?: string;
    open: boolean;
    onClose: () => void;
    onBack?: () => void;
    backLabel?: string;
}

export const printDigitalSigning = (id: string) => window.open(`${window.location.origin}/print/signature/${id}`, '_blank');

const NewTabLink = ({href, children}: {href: string, children: React.ReactNode}) => (
    <Button
        size={'small'}
        endIcon={<OpenInNewIcon sx={{fontSize: 16}}/>}
        href={href}
        target={'_blank'}
        rel={'noopener noreferrer'}
        sx={{px: 0.5, minHeight: 32}}
    >
        {children}
    </Button>
);

function AuditTrail({details}: {details: DigitalSigningDetailsView}) {
    const {t} = useTranslation();
    const confirmedBy = details.events?.some(event => event.type === 'CONFIRMED') ? undefined : details.confirmedByUserName;

    return (
        <Box component={'section'} aria-labelledby={HISTORY_TITLE_ID} sx={{flexShrink: 0, borderTop: 1, borderColor: 'divider'}}>
            <Stack gap={1.5} sx={{maxWidth: PAGE_WIDTH, mx: 'auto', px: 3, py: 2.5}}>
                <Box component={'h3'} id={HISTORY_TITLE_ID} sx={SECTION_TITLE_SX}>{t('History')}</Box>
                <DigitalSigningHistory events={details.events ?? []}/>
                {!!confirmedBy && (
                    <Typography variant={'caption'} color={'text.secondary'}>
                        {t('Confirmed by {{name}}', {name: confirmedBy})}
                    </Typography>
                )}
                {!!details.templateName && (
                    <Typography variant={'caption'} color={'text.secondary'}>
                        {t('Template: {{name}}', {name: details.templateName})}
                    </Typography>
                )}
                {(!!details.folioId || !!details.reservationId) && (
                    <Stack direction={'row'} flexWrap={'wrap'} gap={1} sx={{ml: -0.5}}>
                        {!!details.folioId && <NewTabLink href={`/system/folios/${details.folioId}`}>{t('Open folio')}</NewTabLink>}
                        {!!details.reservationId && (
                            <NewTabLink href={`/system/reservations/${details.reservationId}`}>{t('Open reservation')}</NewTabLink>
                        )}
                    </Stack>
                )}
            </Stack>
        </Box>
    );
}

const DigitalSigningDetailsDialog = ({id, open, onClose, onBack, backLabel}: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const back = backLabel ?? t('Back to the folio');

    const query = useQuery(
        digitalSigningDetailsKey(id),
        () => DigitalSigningService.details(id!),
        {enabled: open && !!id, refetchOnWindowFocus: false, retry: 1}
    );
    const details = query.data?.id === id ? query.data : undefined;
    const pageShown = !!details?.snapshot;

    const endRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        if (open && pageShown) endRef.current?.scrollIntoView?.({block: 'end'});
    }, [open, details?.id, pageShown]);

    const page = () => {
        if (query.isError && !details) {
            return (
                <Box sx={{p: 3}}>
                    <Alert severity={'error'} action={<Button color={'inherit'} size={'small'} onClick={() => query.refetch()}>{t('Retry')}</Button>}>
                        {t('The signature could not be loaded.')}
                    </Alert>
                </Box>
            );
        }
        if (!details) {
            return (
                <Stack gap={2} sx={{maxWidth: 600, mx: 'auto', px: 3, py: 5}}>
                    <Skeleton variant={'rounded'} height={56} width={160} sx={{mx: 'auto'}}/>
                    <Skeleton variant={'rounded'} height={44}/>
                    <Skeleton variant={'rounded'} height={200}/>
                    <Skeleton variant={'rounded'} height={180}/>
                </Stack>
            );
        }
        if (!details.snapshot) {
            return (
                <Box sx={{p: 3}}>
                    <Alert severity={'warning'}>{t('The signed page could not be read.')}</Alert>
                </Box>
            );
        }

        return <DigitalSigningSignedView snapshot={details.snapshot} {...signedPageOf(details)}/>;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            fullScreen={fullScreen}
            aria-labelledby={DIGITAL_SIGNING_DETAILS_TITLE_ID}
            PaperProps={{sx: PAPER_SX}}
        >
            <Box sx={{flexShrink: 0, px: {xs: 2, md: 3}, py: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                <Stack direction={'row'} alignItems={'center'} gap={1} sx={{minHeight: 40}}>
                    {!!onBack && (
                        <Box component={'span'} sx={{ml: -1, flexShrink: 0}}>
                            <IconButton aria-label={back} onClick={onBack} sx={{width: 40, height: 40}}>
                                <ArrowBackIcon/>
                            </IconButton>
                        </Box>
                    )}
                    <Box component={'h2'} id={DIGITAL_SIGNING_DETAILS_TITLE_ID} sx={{m: 0, fontSize: 18, lineHeight: '24px', fontWeight: 600, flexShrink: 0}}>
                        {t('Signature')}
                    </Box>
                    {details?.status
                        ? <Box sx={{display: 'flex', flexShrink: 0}}><DigitalSigningStatusElement status={details.status}/></Box>
                        : <Skeleton variant={'rounded'} width={80} height={28}/>}
                    <Box sx={{flex: 1}}/>
                    <IconButton aria-label={t('Print')} onClick={() => id && printDigitalSigning(id)} disabled={!details?.snapshot} sx={{width: 40, height: 40}}>
                        <PrintIcon/>
                    </IconButton>
                    <IconButton aria-label={t('Close')} onClick={onClose} sx={{width: 40, height: 40}}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </Box>

            <Box sx={{flex: '1 1 auto', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column'}}>
                <Box sx={{flex: '1 0 auto', bgcolor: SIGNING_PAPER}}>
                    {page()}
                </Box>
                {!!details && <AuditTrail details={details}/>}
                <Box ref={endRef} sx={{flexShrink: 0}}/>
            </Box>
        </Dialog>
    );
};

export default DigitalSigningDetailsDialog;
